import { useState } from "react";
import { Button } from "../ui/Button";
import { useToast } from "../ui/ToastContainer";
import { courseService } from "../../services/courseService";
import { parseApiError } from "../../utils/errorHandler";
import { AlertTriangle } from "lucide-react";
import { Modal } from "../ui/Modal";

interface Course {
  _id: string;
  title: string;
  totalModules?: number;
  totalLessons?: number;
  enrollmentCount?: number;
}

interface DeleteCourseDialogProps {
  isOpen: boolean;
  course: Course | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function DeleteCourseDialog({
  isOpen,
  course,
  onClose,
  onSuccess,
}: DeleteCourseDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  if (!isOpen || !course) return null;

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await courseService.deleteCourse(course._id);
      showSuccess(`Course "${course.title}" deleted successfully`, "Success");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to delete course:", err);
      const apiError = parseApiError(err);
      setError(apiError.message);
      showError(apiError.message, "Delete Failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const hasEnrollments = (course.enrollmentCount || 0) > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Course"
      className="max-w-md"
    >
      <div className="space-y-4">
        <div className="flex items-center gap-4 text-red-600 bg-red-50 p-4 rounded-lg mb-4">
          <AlertTriangle size={24} />
          <p className="font-semibold">Warning: Destructive Action</p>
        </div>

        <p className="text-gray-600">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-gray-900">"{course.title}"</span>?
        </p>

        {hasEnrollments && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This course has {course.enrollmentCount}{" "}
              enrolled students. Deleting it may affect their access and
              progress.
            </p>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-500">This action will:</p>
          <ul className="text-sm text-gray-600 mt-2 space-y-1">
            <li>• Delete all {course.totalModules || 0} modules</li>
            <li>• Delete all {course.totalLessons || 0} lessons</li>
            <li>• Remove all enrollment records</li>
            <li>• This action cannot be undone</li>
          </ul>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? "Deleting..." : "Delete Course"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
